import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import Layout from "@/components/layout/Layout";
import api from "@/lib/api";
import { GreenButton, GhostButton } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const NEXT = {
    gang: ["Your Wholesale pass is live", "Discounts are already applied at checkout — order something.", "/shirts/white", "Start an order"],
    mall: ["Your shop space is open", "Now let's set the shop up — nine short questions, or one button that opens everything at once.", "/account?setup=1", "Set up my store"],
    boost: ["Boost is live", "Your listing sits at the top of its tab for the next 7 days.", "/account/listings", "See my listings"],
    order: ["Paid — you're in production", "We've got your money and your art. Your job moved straight into production and you'll get a text when it's ready for pickup.", "/", "Back home"],
};

export default function PaymentResult({ cancelled = false }) {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const { clearStore } = useCart();
    const sessionId = sp.get("session_id");
    const [state, setState] = useState(cancelled ? "cancelled" : "checking");
    const [kind, setKind] = useState("gang");
    const [orderId, setOrderId] = useState(null);

    useEffect(() => {
        if (cancelled || !sessionId) return;
        let tries = 0;
        const poll = async () => {
            tries += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                const key = data.lookup_key || "";
                setKind(key === "print_order" ? "order" : key.startsWith("mall") ? "mall" : key.startsWith("boost") ? "boost" : "gang");
                if (data.order_id) setOrderId(data.order_id);
                if (data.payment_status === "paid") {
                    setState("paid");
                    refresh?.();
                    // the paid shop's items leave the cart only now that Stripe has confirmed
                    try {
                        const pending = JSON.parse(sessionStorage.getItem("pending_pay") || "null");
                        if (pending?.slug && (!data.order_id || pending.order_id === data.order_id)) clearStore(pending.slug);
                    } catch { /* nothing pending */ }
                    sessionStorage.removeItem("pending_pay");
                    // 7.8: a paid print order lands on its confirmation page
                    if (key === "print_order" && data.order_id) navigate(`/order-confirmation/${data.order_id}`, { replace: true });
                    return;
                }
            } catch { /* keep polling */ }
            if (tries > 12) { setState("timeout"); return; }
            setTimeout(poll, 2000);
        };
        poll();
    }, [sessionId, cancelled, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

    // 7.1 #4: Stripe cancel returns to the cart with every line still there
    if (cancelled) return <Navigate to="/cart" replace />;

    const [title, blurb, to, cta] = NEXT[kind];
    const isOrder = kind === "order";

    return (
        <Layout hideTicker>
            <div className="mx-auto max-w-2xl px-4 py-24 text-center">
                {state === "checking" && (
                    <div data-testid="payment-checking">
                        <Loader2 size={40} className="mx-auto animate-spin text-brand-green" />
                        <h1 className="mt-6 font-display text-3xl font-black tracking-tight">Confirming with Stripe…</h1>
                        <p className="mt-2 text-zinc-400">Hang on — this takes a couple of seconds.</p>
                    </div>
                )}
                {state === "paid" && (
                    <div data-testid="payment-success">
                        <Check size={44} className="mx-auto text-brand-green" />
                        <h1 className="mt-5 font-display text-3xl font-black tracking-tight sm:text-4xl">{title}</h1>
                        <p className="mt-3 text-zinc-300">
                            {blurb}{isOrder ? "" : " Your first month is 50% off. You can cancel any time from your account."}
                        </p>
                        <div className="mt-7 flex flex-wrap justify-center gap-3">
                            {isOrder && orderId ? (
                                <Link to={`/order-confirmation/${orderId}`}><GreenButton data-testid="payment-order-link">View my order</GreenButton></Link>
                            ) : (
                                <Link to={to}><GreenButton data-testid="payment-next">{cta}</GreenButton></Link>
                            )}
                            <Link to="/account"><GhostButton data-testid="payment-account">My account</GhostButton></Link>
                        </div>
                    </div>
                )}
                {state === "cancelled" && (
                    <div data-testid="payment-cancelled">
                        <X size={44} className="mx-auto text-zinc-500" />
                        <h1 className="mt-5 font-display text-3xl font-black tracking-tight">No charge made.</h1>
                        <p className="mt-3 text-zinc-400">You left Stripe before paying, so nothing was billed. Your cart is untouched.</p>
                        <div className="mt-7 flex flex-wrap justify-center gap-3">
                            <Link to="/directory"><GreenButton data-testid="payment-back-mall">Back to the directory</GreenButton></Link>
                            <Link to="/wholesale"><GhostButton>Wholesale passes</GhostButton></Link>
                        </div>
                    </div>
                )}
                {state === "timeout" && (
                    <div data-testid="payment-timeout">
                        <h1 className="font-display text-3xl font-black tracking-tight">Still processing.</h1>
                        <p className="mt-3 text-zinc-400">Stripe hasn't confirmed yet. Check your account in a minute, or text us at (347) 573-3875 and we'll sort it.</p>
                        <Link to="/account" className="mt-6 inline-block"><GreenButton>My account</GreenButton></Link>
                    </div>
                )}
            </div>
        </Layout>
    );
}
